import { Component, EventEmitter, Input, Output } from "@angular/core";
import { AbstractControl, UntypedFormArray, UntypedFormGroup } from "@angular/forms";

import { Observable, of } from "rxjs";
import { tap } from "rxjs/operators";

import { EntitetStatus } from "@dffedb/util";

import { NoteBruger, NoteHenvendelsestype, NoteOmraade, NoteOpgavestyringStatus, opretTomNote } from "../model/note.model";
import { NoteDialog } from "../note-dialog/note-dialog.component";
import { NoteFormBuilder } from "../shared/note-form-builder";

type FormGroupFunc = (formGroup: UntypedFormGroup) => Observable<UntypedFormGroup>;
const defaultIngenNoterTekst = "<p>Der er ikke oprettet nogen noter</p>";

@Component({
    selector: "simpel-note-liste",
    templateUrl: "./simpel-note-liste.component.html",
    styleUrls: ["./simpel-note-liste.component.scss"]
})
export class SimpelNoteListeComponent {
    public get synligeNoter(): AbstractControl[] {
        return (this.formArray && this.formArray.controls.filter((c) => c.get("status").value !== EntitetStatus.Slettet)) || [];
    }

    @Input() public defaultOmraadeId: number;
    @Input() public omraader: NoteOmraade[];
    @Input() public henvendelsestyper: NoteHenvendelsestype[];
    @Input() public opgavestyringStatusser: NoteOpgavestyringStatus[];
    @Input() public brugere: NoteBruger[];

    @Input() public formArray: UntypedFormArray;
    @Input() public ingenNoterTekst: string = defaultIngenNoterTekst;
    @Input() public maxNoter = 99999;

    @Input() public slet: FormGroupFunc;
    @Input() public gem: FormGroupFunc;
    @Output() public initialiserNote: EventEmitter<UntypedFormGroup> = new EventEmitter();

    constructor(private readonly noteDialog: NoteDialog) {}

    public onOpretClick(): void {
        // Burde vi lave en NoteFormBuilder.cloneFormGroup()?
        // Den bliver måske lidt tricky, da vi skal bruge omraader/henvendelsestyper/brugere.
        const nyNote = opretTomNote(this.defaultOmraadeId);
        const nyNoteFormGroup = NoteFormBuilder.noteToFormGroup(
            nyNote,
            this.omraader,
            this.henvendelsestyper,
            this.opgavestyringStatusser,
            this.brugere
        );
        this.initialiserNote.emit(nyNoteFormGroup);
        this.aabnNoteDialog(nyNoteFormGroup);
    }

    public onRedigerClick(noteFormGroup: UntypedFormGroup): void {
        this.aabnNoteDialog(noteFormGroup);
    }
    private dummyFormGroupFunc: FormGroupFunc = (formGroup: UntypedFormGroup) => of(formGroup);

    private aabnNoteDialog(oprindeligNoteFormGroup: UntypedFormGroup): void {
        // Vi opretter en kopi af noten, så det er muligt at annullere note-dialogen og dermed skippe ændringerne.
        const kopi = NoteFormBuilder.noteToFormGroup(
            NoteFormBuilder.formGroupToNote(oprindeligNoteFormGroup),
            this.omraader,
            this.henvendelsestyper,
            this.opgavestyringStatusser,
            this.brugere
        );

        this.noteDialog.open(kopi, {
            gem: (redigeretNoteFormGroup: UntypedFormGroup) => this.gemNote(oprindeligNoteFormGroup, redigeretNoteFormGroup),
            slet: (redigeretNoteFormGroup: UntypedFormGroup) => this.sletNote(oprindeligNoteFormGroup, redigeretNoteFormGroup)
        });
    }

    private gemNote(oprindeligNoteFormGroup: UntypedFormGroup, redigeretNoteFormGroup: UntypedFormGroup): Observable<UntypedFormGroup> {
        const gem = this.gem || this.dummyFormGroupFunc;
        return gem(redigeretNoteFormGroup).pipe(
            tap((result: UntypedFormGroup) => {
                const erNyNote = oprindeligNoteFormGroup.value.noteId === 0;
                // Påtryk ændrede værdier.
                NoteFormBuilder.setControls(oprindeligNoteFormGroup, result.controls);
                // Har gem() ikke tildelt en ny noteId? Så sætter vi en negativ værdi for at indikere at det er en memory-only note.
                if (oprindeligNoteFormGroup.value.noteId === 0) {
                    oprindeligNoteFormGroup.get("noteId").setValue(this.genererNegativNoteId());
                }
                // Er det en ny note: Så skal den tilføjes til note-listen.
                if (erNyNote) {
                    this.formArray.insert(this.formArray.length, oprindeligNoteFormGroup);
                }
            })
        );
    }

    private sletNote(oprindeligNoteFormGroup: UntypedFormGroup, redigeretNoteFormGroup: UntypedFormGroup): Observable<UntypedFormGroup> {
        const slet = this.slet || this.dummyFormGroupFunc;
        return slet(redigeretNoteFormGroup).pipe(
            tap((result: UntypedFormGroup) => {
                // Sæt status til Slettet, så noten bliver usynlig for brugeren.
                oprindeligNoteFormGroup.get("status").setValue(EntitetStatus.Slettet);

                // Vi kan ikke altid tillade os at fjerne noten fra listen,
                // da vi i nogle situationer samler op på nye / redigerede / slettede noter.
                // Dette er f.eks. tilfældet i forbindelse med Servicerapporter.
                // Hvis gem() returnerer 'null', betyder det dog at den kaldte rutine har valgt, vi må slette noten fra listen.
                // Hvis noteId <= 0, er det en memory-only note, og vi kan ligeledes slette den fra listen.
                const fjernFraListen = !result || result.value.noteId <= 0;
                if (fjernFraListen) {
                    const index = this.formArray.controls.indexOf(oprindeligNoteFormGroup);
                    this.formArray.removeAt(index);
                }
            })
        );
    }

    private genererNegativNoteId(): number {
        const result = this.formArray.controls.reduce((previous, noteFormGroup) => Math.min(previous, noteFormGroup.value.noteId), 0) - 1;
        return result;
    }
}
